/**
 * План магазина со шкафами.
 *
 * У объекта может быть несколько планов — этажи и зоны («Торговый зал»,
 * «Склад»). Подложка — картинка плана (PNG, JPEG, WebP); без неё — пустая
 * сетка, на которой шкафы расставляются схематично.
 *
 * Смотрят все: нажатие на шкаф открывает его содержимое. Редактор ставит
 * шкаф на план (выбрать шкаф в списке → нажать на плане), передвигает его
 * перетаскиванием и убирает с плана из списка. Координаты — доли ширины
 * и высоты плана, поэтому от размера экрана не зависят.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api, apiFetch, fetchObjectUrl } from '../../api/client';
import { PLANS_EVENT } from '../../api/liveUpdates';
import { clamp01, findMarker, pointToPlan, shortCabinetName, unplacedCabinets } from '../../domain/storePlan';
import Icon from '../../components/common/Icon';
import ui from '../../components/common/ui.module.css';
import styles from './StorePlan.module.css';

const IMAGE_MAX_MB = 10;
const ZOOMS = [1, 1.5, 2, 3];

/** Шкаф на плане. Редактор передвигает его перетаскиванием. */
function Marker({ marker, cabinet, canEdit, highlight, frameRef, onOpen, onMove }) {
  const [drag, setDrag] = useState(null);
  const gesture = useRef(null);
  const dragged = useRef(false);
  const pos = drag || marker;

  function down(e) {
    if (!canEdit || e.button > 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    // Запоминаем, где схватили относительно острия метки: метка едет
    // вместе с пальцем, а не прыгает острием под него
    const rect = frameRef.current.getBoundingClientRect();
    gesture.current = {
      x: e.clientX, y: e.clientY, moved: false,
      dx: rect.left + marker.x * rect.width - e.clientX,
      dy: rect.top + marker.y * rect.height - e.clientY,
    };
    dragged.current = false;
  }

  function move(e) {
    const g = gesture.current;
    if (!g) return;
    if (!g.moved && Math.hypot(e.clientX - g.x, e.clientY - g.y) < 5) return;
    g.moved = true;
    setDrag(pointToPlan(e.clientX + g.dx, e.clientY + g.dy, frameRef.current.getBoundingClientRect()));
  }

  function up() {
    const g = gesture.current;
    gesture.current = null;
    if (g && g.moved && drag) {
      dragged.current = true;
      onMove(drag);
    }
    setDrag(null);
  }

  return (
    <button
      type="button"
      className={[styles.marker, highlight ? styles.markerHighlight : '', drag ? styles.markerDrag : '',
        canEdit ? styles.markerMovable : ''].filter(Boolean).join(' ')}
      style={{ left: `${pos.x * 100}%`, top: `${pos.y * 100}%` }}
      title={canEdit ? `${cabinet.name} — открыть или перетащить` : cabinet.name}
      aria-label={`${cabinet.name}: открыть шкаф`}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={() => { gesture.current = null; setDrag(null); }}
      onClick={() => { if (dragged.current) { dragged.current = false; return; } onOpen(cabinet.id); }}
    >
      <span className={styles.markerLabel}>{shortCabinetName(cabinet.name)}</span>
      <span className={styles.markerPin} aria-hidden="true" />
    </button>
  );
}

export default function StorePlan({ mag, cabinets, canEdit, highlightId, onOpenCabinet }) {
  const [plans, setPlans] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [placing, setPlacing] = useState(null);       // id шкафа, который ставим на план
  const [zoom, setZoom] = useState(1);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [naming, setNaming] = useState(null);          // { mode: 'new' | 'rename', value }
  const [confirmDelete, setConfirmDelete] = useState(false);
  const frameRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async (selectId) => {
    try {
      const list = await api.get(`/api/plans?mag=${encodeURIComponent(mag)}`);
      setPlans(list);
      setActiveId((cur) => {
        const want = selectId ?? cur;
        if (want && list.some((p) => p.id === want)) return want;
        // Пришли «показать шкаф на плане» — открыть план, где он стоит
        const withHighlight = highlightId && findMarker(list, highlightId);
        return withHighlight ? withHighlight.plan.id : list[0]?.id ?? null;
      });
    } catch (e) {
      setError(e.message);
      setPlans([]);
    }
  }, [mag, highlightId]);

  useEffect(() => { setPlans(null); setActiveId(null); load(); }, [load]);

  // Другой редактор поменял планы этого объекта — перечитать
  useEffect(() => {
    const onChange = (e) => { if (e.detail?.mag === mag) load(); };
    window.addEventListener(PLANS_EVENT, onChange);
    return () => window.removeEventListener(PLANS_EVENT, onChange);
  }, [mag, load]);

  const plan = plans?.find((p) => p.id === activeId) || null;

  // Подложка: картинка запрашивается с токеном и показывается по ссылке blob:
  useEffect(() => {
    if (!plan || !plan.hasImage) { setImageUrl(null); return undefined; }
    let url = null;
    let alive = true;
    fetchObjectUrl(`/api/plans/${plan.id}/image?v=${plan.updatedAt}`)
      .then((u) => { url = u; if (alive) setImageUrl(u); else URL.revokeObjectURL(u); })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [plan?.id, plan?.hasImage, plan?.updatedAt]);

  useEffect(() => {
    if (!placing) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPlacing(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [placing]);

  const byId = useMemo(() => new Map(cabinets.map((c) => [c.id, c])), [cabinets]);
  const unplaced = useMemo(() => (plans ? unplacedCabinets(cabinets, plans) : []), [cabinets, plans]);

  async function run(action) {
    setBusy(true);
    setError(null);
    try { await action(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  const saveMarker = (cabinetId, { x, y }) => run(async () => {
    // Сразу на экране, не дожидаясь сервера: перетаскивание не «прыгает»
    setPlans((list) => list.map((p) => ({
      ...p,
      markers: [...p.markers.filter((m) => m.cabinetId !== cabinetId),
        ...(p.id === plan.id ? [{ cabinetId, x: clamp01(x), y: clamp01(y) }] : [])],
    })));
    await api.put(`/api/plans/${plan.id}/markers/${cabinetId}`, { x, y });
    await load();
  });

  const removeMarker = (cabinetId) => {
    const found = findMarker(plans, cabinetId);
    if (!found) return;
    run(async () => { await api.delete(`/api/plans/${found.plan.id}/markers/${cabinetId}`); await load(); });
  };

  function placeAt(e) {
    if (!placing || !plan) return;
    const point = pointToPlan(e.clientX, e.clientY, frameRef.current.getBoundingClientRect());
    const id = placing;
    setPlacing(null);
    saveMarker(id, point);
  }

  function submitName(e) {
    e.preventDefault();
    const name = naming.value.trim();
    if (!name) return;
    run(async () => {
      if (naming.mode === 'new') {
        const { id } = await api.post('/api/plans', { mag, name });
        setNaming(null);
        await load(id);
      } else {
        await api.put(`/api/plans/${plan.id}`, { name });
        setNaming(null);
        await load();
      }
    });
  }

  function uploadImage(file) {
    if (!file) return;
    if (file.size > IMAGE_MAX_MB * 1024 * 1024) {
      setError(`Картинка больше ${IMAGE_MAX_MB} МБ — уменьшите её или сохраните в JPEG`);
      return;
    }
    run(async () => {
      await apiFetch(`/api/plans/${plan.id}/image`, {
        method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      await load();
    });
  }

  function deletePlan() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 4000);
      return;
    }
    setConfirmDelete(false);
    run(async () => { await api.delete(`/api/plans/${plan.id}`); setActiveId(null); await load(); });
  }

  if (!plans) return <div className={styles.loading}>Загружаю план…</div>;

  const nameForm = naming && (
    <form className={styles.nameForm} onSubmit={submitName}>
      <input
        className={ui.search}
        value={naming.value}
        onChange={(e) => setNaming({ ...naming, value: e.target.value })}
        placeholder="Например, «Торговый зал» или «Склад»"
        aria-label="Название плана"
        maxLength={80}
        autoFocus
      />
      <button className={`${ui.button} ${ui.primary}`} type="submit" disabled={busy}>
        {naming.mode === 'new' ? 'Создать' : 'Сохранить'}
      </button>
      <button className={ui.button} type="button" onClick={() => setNaming(null)}>Отмена</button>
    </form>
  );

  if (plans.length === 0) {
    return (
      <div className={styles.emptyBox}>
        <div className={styles.emptyTitle}>Плана магазина ещё нет</div>
        <p className={styles.emptyText}>
          {canEdit
            ? 'Создайте план, загрузите картинку этажа (PNG, JPEG или WebP, до 10 МБ) и отметьте на ней шкафы. Картинки нет — шкафы можно расставить на пустой сетке.'
            : 'Когда редактор загрузит план и отметит шкафы, он появится здесь.'}
        </p>
        {error && <div className={styles.error}>{error}</div>}
        {canEdit && (naming ? nameForm : (
          <button className={`${ui.button} ${ui.primary}`} onClick={() => setNaming({ mode: 'new', value: 'Торговый зал' })}>
            Создать план
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      {/* ── Планы объекта и действия ── */}
      <div className={styles.bar}>
        <div className={styles.tabs} role="tablist" aria-label="Планы объекта">
          {plans.map((p) => (
            <button
              key={p.id}
              role="tab"
              aria-selected={p.id === activeId}
              className={`${styles.tab} ${p.id === activeId ? styles.tabActive : ''}`}
              onClick={() => { setActiveId(p.id); setPlacing(null); setZoom(1); }}
            >
              {p.name}
              <span className={styles.tabCount}>{p.markers.length}</span>
            </button>
          ))}
          {canEdit && !naming && (
            <button className={styles.tabAdd} onClick={() => setNaming({ mode: 'new', value: '' })} title="Добавить план — этаж или зону">
              + План
            </button>
          )}
        </div>

        <div className={styles.tools}>
          <div className={styles.zoom} role="group" aria-label="Масштаб">
            <button type="button" onClick={() => setZoom(ZOOMS[Math.max(0, ZOOMS.indexOf(zoom) - 1)])} disabled={zoom === ZOOMS[0]} aria-label="Мельче">−</button>
            <span>{Math.round(zoom * 100)}%</span>
            <button type="button" onClick={() => setZoom(ZOOMS[Math.min(ZOOMS.length - 1, ZOOMS.indexOf(zoom) + 1)])} disabled={zoom === ZOOMS[ZOOMS.length - 1]} aria-label="Крупнее">+</button>
          </div>
          {canEdit && plan && (
            <>
              <button className={ui.button} onClick={() => fileRef.current?.click()} disabled={busy}>
                <Icon name="upload" size={16} />
                {plan.hasImage ? 'Заменить картинку' : 'Загрузить картинку'}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                onChange={(e) => { uploadImage(e.target.files?.[0]); e.target.value = ''; }}
              />
              <button className={ui.button} onClick={() => setNaming({ mode: 'rename', value: plan.name })}>Переименовать</button>
              <button className={`${ui.button} ${confirmDelete ? styles.dangerOn : styles.danger}`} onClick={deletePlan} disabled={busy}>
                {confirmDelete ? 'Точно удалить?' : 'Удалить'}
              </button>
            </>
          )}
        </div>
      </div>

      {nameForm}
      {error && <div className={styles.error}>{error}</div>}
      {placing && (
        <div className={styles.placingHint} role="status">
          Нажмите на плане, где стоит «{byId.get(placing)?.name}».
          <button type="button" className={styles.linkButton} onClick={() => setPlacing(null)}>Отмена</button>
        </div>
      )}

      <div className={styles.layout}>
        {/* ── Сам план ── */}
        {plan && (
          <div className={styles.scroller}>
            <div
              ref={frameRef}
              className={`${styles.frame} ${plan.hasImage ? '' : styles.grid} ${placing ? styles.framePlacing : ''}`}
              style={{ width: `${zoom * 100}%`, aspectRatio: `${plan.width} / ${plan.height}` }}
              onClick={placeAt}
            >
              {plan.hasImage && imageUrl && <img className={styles.image} src={imageUrl} alt={`План: ${plan.name}`} draggable={false} />}
              {!plan.hasImage && plan.markers.length === 0 && !placing && (
                <div className={styles.gridHint}>
                  {canEdit ? 'Картинки нет — загрузите её или расставьте шкафы на сетке.' : 'Шкафы на этом плане ещё не отмечены.'}
                </div>
              )}
              {plan.markers.map((m) => {
                const cabinet = byId.get(m.cabinetId);
                if (!cabinet) return null;
                return (
                  <Marker
                    key={m.cabinetId}
                    marker={m}
                    cabinet={cabinet}
                    canEdit={canEdit && !placing}
                    highlight={m.cabinetId === highlightId}
                    frameRef={frameRef}
                    onOpen={onOpenCabinet}
                    onMove={(point) => saveMarker(m.cabinetId, point)}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* ── Шкафы объекта: что на плане, что нет — для редактора ── */}
        {canEdit && (
          <aside className={styles.side} aria-label="Шкафы объекта">
            <div className={styles.sideTitle}>
              Шкафы объекта
              {unplaced.length > 0 && <span className={styles.sideNote}>не на плане: {unplaced.length}</span>}
            </div>
            <ul className={styles.cabList}>
              {cabinets.map((c) => {
                const where = findMarker(plans, c.id);
                return (
                  <li key={c.id} className={`${styles.cabRow} ${placing === c.id ? styles.cabRowActive : ''}`}>
                    <span className={styles.cabName} title={c.name}>{c.name}</span>
                    <span className={styles.cabWhere}>
                      {where ? (where.plan.id === activeId ? 'на этом плане' : `на плане «${where.plan.name}»`) : 'не на плане'}
                    </span>
                    {where ? (
                      <button type="button" className={styles.linkButton} onClick={() => removeMarker(c.id)} disabled={busy}>Убрать</button>
                    ) : (
                      <button type="button" className={styles.linkButton} onClick={() => setPlacing(placing === c.id ? null : c.id)}>
                        {placing === c.id ? 'Отмена' : 'Поставить'}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
            <p className={styles.sideHelp}>Шкаф на плане можно перетащить. Нажатие открывает его содержимое.</p>
          </aside>
        )}
      </div>
    </div>
  );
}
